import { useEffect, useId, useRef, useState } from 'react';
import type { PlacePrediction } from '@bilmat/shared';
import { useAutocomplete } from '../hooks/useAutocomplete';
import { RouteMarker } from './RouteMarker';
import { LocateIcon } from './LocateIcon';

type Props = {
  label: string;
  placeholder: string;
  value: string;
  variant: 'origin' | 'dest';
  onChange: (value: string) => void;
  onSelect: (prediction: PlacePrediction) => void;
  onUseLocation?: () => void;
  locating?: boolean;
};

export function AutocompleteInput({
  label,
  placeholder,
  value,
  variant,
  onChange,
  onSelect,
  onUseLocation,
  locating = false,
}: Props) {
  const inputId = useId();
  const [focused, setFocused] = useState(false);
  // Göm-timern måste avbrytas när fältet återfår fokus — annars kan ett
  // blur→fokus-par inom 150 ms (iOS-tangentbordets ▲/▼-pilar, snabba
  // dubbeltryck) lämna focused=false fast fältet är fokuserat, och då
  // visas förslagslistan aldrig hur mycket man än skriver.
  const hideTimer = useRef<number | null>(null);
  const cancelHide = (): void => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const hideSoon = (): void => {
    cancelHide();
    // Fördröjningen låter tryck på ett förslag hinna landa före gömningen.
    hideTimer.current = window.setTimeout(() => {
      hideTimer.current = null;
      setFocused(false);
    }, 150);
  };
  useEffect(() => cancelHide, []);
  const { predictions, isLoading, error, newSession } = useAutocomplete(value);

  const showResults = focused && predictions.length > 0;
  const hasText = value.length > 0;
  const showClear = hasText && !locating;
  const showLocate = !hasText && !locating && !!onUseLocation;
  const hasAction = showClear || showLocate || locating;

  return (
    <div className={`autocomplete${hasAction ? ' autocomplete--action' : ''}`}>
      <RouteMarker variant={variant} className="field-dot" />
      <label className="input-label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        type="text"
        className="text-input"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        name={inputId}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => {
          cancelHide();
          setFocused(true);
          e.target.select();
        }}
        onBlur={hideSoon}
      />
      {hasAction && (
        <button
          type="button"
          className={`field-action${showClear ? ' field-action--clear' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={showClear ? () => onChange('') : onUseLocation}
          disabled={locating}
          aria-label={showClear ? 'Rensa' : 'Använd min position'}
          title={showClear ? 'Rensa' : 'Använd min position'}
        >
          {locating ? (
            <span className="field-action-spin" aria-hidden />
          ) : showClear ? (
            // iOS-stil: liten ifylld grå cirkel med vitt kryss (xmark.circle.fill).
            <svg viewBox="0 0 16 16" width="19" height="19" aria-hidden focusable="false">
              <circle cx="8" cy="8" r="8" fill="currentColor" />
              <path
                d="M5.4 5.4l5.2 5.2M10.6 5.4l-5.2 5.2"
                stroke="#fff"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <LocateIcon />
          )}
        </button>
      )}
      {isLoading && <div className="autocomplete-hint">Söker…</div>}
      {error && <div className="autocomplete-error">{error}</div>}
      {showResults && (
        <ul className="autocomplete-list" role="listbox">
          {predictions.map((p) => (
            <li key={p.placeId}>
              <button
                type="button"
                className="autocomplete-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(p);
                  newSession();
                  cancelHide();
                  setFocused(false);
                }}
              >
                <span className="autocomplete-main">{p.mainText}</span>
                <span className="autocomplete-secondary">{p.secondaryText}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
